/**
 * `/pages/:slug` — the hosted page's full profile (contractor pages phase 1):
 * contact and the Call button, service area, trades, services, photos,
 * about and credentials, hours, testimonials, FAQ and the calls to action,
 * with the API's completeness score and a checklist of what is missing.
 *
 * Data: `GET /partner/hosted-pages/:slug/profile` → page, profile, assets,
 * completeness, trade_options, quote_form. Each card (hostedPage/sections.tsx)
 * saves its own keys with `PUT …/profile` (every key optional, arrays
 * replace) and gets the same shape back, which refreshes the score; photos
 * save as they are made (hostedPage/Photos.tsx). The public page is cached
 * for 60 s, so the screen says changes show within a minute.
 *
 * Owner and admin only (roles.ts `hostedPageProfile`), like the API.
 */
import { useRef } from 'react';
import type { HostedPageAsset, HostedPageProfileResponse } from '../api/types';
import { useSession } from '../auth/session';
import { LeadReadiness } from '../hostedPage/LeadReadiness';
import { CompletenessChecklist, CompletenessRing, clampScore } from '../hostedPage/Completeness';
import { normaliseProfile, serviceAreaWire, type ServiceAreaWire } from '../hostedPage/model';
import { PhotosSection } from '../hostedPage/Photos';
import {
  AboutSection,
  ContactSection,
  CtaSection,
  FaqSection,
  HoursSection,
  ServiceAreaSection,
  ServicesSection,
  TestimonialsSection,
  TradesSection,
} from '../hostedPage/sections';
import { Link } from '../router';
import { Empty, ErrorNotice, Loading, PageHead } from '../ui/kit';
import { useQuery } from '../ui/hooks';
import { WIDGET_PAGE_PATH, WidgetPageLink } from './WidgetPageShare';

type Loaded = HostedPageProfileResponse & { areaWire: ServiceAreaWire };

/** The response with every profile key present (see model.ts `normaliseProfile`). */
function normalise(res: HostedPageProfileResponse): Loaded {
  return {
    ...res,
    areaWire: serviceAreaWire(res.profile),
    profile: normaliseProfile(res.profile),
    assets: Array.isArray(res.assets) ? res.assets : [],
    completeness: { score: clampScore(res.completeness?.score), missing: res.completeness?.missing ?? [] },
    trade_options: res.trade_options ?? [],
    quote_form: res.quote_form ?? { enabled: false, reason: null },
  };
}

export function HostedPageProfile({ slug }: { slug: string }) {
  const { api } = useSession();
  const query = useQuery(async () => normalise(await api.getHostedPageProfile(slug)), ['hosted-page-profile', slug]);
  const latest = useRef<Loaded | null>(null);
  latest.current = query.data;
  const data = query.data;

  const onSaved = (res: HostedPageProfileResponse) => {
    const next = normalise(res);
    latest.current = next;
    query.setData(next);
  };
  const updateAssets = (fn: (prev: HostedPageAsset[]) => HostedPageAsset[]) => {
    const cur = latest.current;
    if (!cur) return;
    const next = { ...cur, assets: fn(cur.assets) };
    latest.current = next;
    query.setData(next);
  };

  const eyebrow = <Link to={WIDGET_PAGE_PATH}>Your page</Link>;

  if (query.loading && !data) {
    return (
      <>
        <PageHead title="Your page" eyebrow={eyebrow} />
        <Loading label="Loading your page" />
      </>
    );
  }
  if (!data) {
    const gone = query.error?.code === 'HOSTED_PAGE_NOT_FOUND' || query.error?.status === 404;
    return (
      <>
        <PageHead title="Your page" eyebrow={eyebrow} />
        {gone ? (
          <Empty level={2} title="We couldn’t find that page">
            <span className="mono wrap-anywhere">finderlyfix.com/p/{slug}</span> isn’t one of your pages. <Link to={WIDGET_PAGE_PATH}>See your pages</Link>.
          </Empty>
        ) : (
          <div className="stack">
            <ErrorNotice error={query.error} />
            <div>
              <button type="button" className="btn" onClick={() => void query.reload()}>
                Try again
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  const { page, profile, assets, completeness, trade_options, quote_form, areaWire } = data;
  const common = { slug, profile, onSaved };
  return (
    <>
      <PageHead
        title="Your page"
        eyebrow={eyebrow}
        lede="Everything a homeowner looks for before they call: your number, what you do, where, when, and proof you’re good at it."
      />
      <div className="stack-lg" style={{ minWidth: 0 }}>
        <section className="panel panel-pad stack" aria-labelledby="hp-summary-title">
          <h2 id="hp-summary-title" className="visually-hidden">
            Your page at a glance
          </h2>
          {page.enabled && page.served ? (
            <WidgetPageLink page={page} />
          ) : (
            <div className="notice warn" role="status">
              <div className="stack-sm" style={{ gap: 2 }}>
                <strong>
                  <span className="mono wrap-anywhere">{page.url}</span> isn’t showing yet.
                </strong>
                <span>
                  {page.enabled
                    ? 'It’s switched on, but its live site key isn’t enabled. You can still fill it in here.'
                    : 'It’s switched off. You can fill it in now and turn it on when you’re ready.'}{' '}
                  <Link to={WIDGET_PAGE_PATH}>Your page</Link>
                </span>
              </div>
            </div>
          )}
          <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <CompletenessRing score={completeness.score} />
            <div className="stack-sm" style={{ flex: '1 1 220px', minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 600 }}>Your page is {completeness.score}% complete</p>
              <CompletenessChecklist missing={completeness.missing} />
            </div>
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            Changes show on your page within a minute.
          </p>
        </section>

        <LeadReadiness slug={slug} facts={{ completeness, quote_form }} />

        <ContactSection {...common} />
        <ServiceAreaSection {...common} wire={areaWire} />
        <TradesSection {...common} options={trade_options} />
        <ServicesSection {...common} />
        <PhotosSection
          slug={slug}
          assets={assets}
          logoAssetId={profile.logo_asset_id}
          updateAssets={updateAssets}
          onProfileSaved={onSaved}
          onRefresh={() => void query.reload()}
        />
        <AboutSection {...common} />
        <HoursSection {...common} />
        <TestimonialsSection {...common} />
        <FaqSection {...common} />
        <CtaSection {...common} quoteForm={quote_form} />
      </div>
    </>
  );
}
